import React from 'react'
import { Link } from 'react-router-dom'
import { useLocale } from '../contexts/LocaleContext'

const TIERS = ['starter', 'professional', 'enterprise'] as const
const FAQS = ['trial', 'billing', 'switching', 'data'] as const

interface PricingProps {
  /** The standalone pricing page repeats the questions; the landing page does not. */
  showFaq?: boolean
}

const Pricing: React.FC<PricingProps> = ({ showFaq = false }) => {
  const { t } = useLocale()

  return (
    <section
      id="prijzen"
      className="border-y border-border bg-surface-card px-5 py-16 sm:px-8 lg:px-14 lg:py-[88px]"
    >
      <div className="mx-auto max-w-[1080px]">
        <h2 className="text-center font-display text-[30px] font-bold tracking-display sm:text-[38px]">
          {t('pricing.title')}
        </h2>
        <p className="mt-3.5 text-center text-[17px] text-text-secondary">
          {t('pricing.subtitle')}
        </p>

        <div className="mt-12 grid items-stretch gap-5 md:grid-cols-3">
          {TIERS.map((tier) => {
            const popular = tier === 'professional'
            const features = t(`pricing.${tier}.features`) as string[]

            return (
              <div
                key={tier}
                className={
                  popular
                    ? 'relative flex flex-col gap-3.5 rounded-lg border-2 border-clay-500 bg-surface-page p-7 shadow-raised'
                    : 'flex flex-col gap-3.5 rounded-lg border border-border bg-surface-page p-7'
                }
              >
                {popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-pill bg-clay-500 px-3.5 py-1 text-xs font-bold text-white">
                    {t('pricing.popular')}
                  </span>
                )}

                <div className="font-display text-lg font-semibold">
                  {t(`pricing.${tier}.name`)}
                </div>
                <div>
                  <span className="font-display text-[40px] font-bold">
                    {t(`pricing.${tier}.price`)}
                  </span>
                  <span className="text-sm text-text-muted"> {t('pricing.perMonth')}</span>
                </div>
                <div className="text-sm font-semibold text-text-secondary">
                  {t(`pricing.${tier}.volunteers`)}
                </div>

                <ul className="text-sm leading-8 text-text-secondary">
                  {features.map((feature) => (
                    <li key={feature}>
                      <span aria-hidden>✓</span> {feature}
                    </li>
                  ))}
                </ul>

                <Link
                  to="/signup"
                  className={
                    popular
                      ? 'mt-auto flex min-h-touch-min items-center justify-center rounded-md bg-action-primary text-[15px] font-semibold text-text-on-primary hover:bg-action-primary-hover'
                      : 'mt-auto flex min-h-touch-min items-center justify-center rounded-md border-[1.5px] border-border-strong text-[15px] font-semibold text-text-primary hover:bg-surface-sunken'
                  }
                >
                  {t('pricing.cta')}
                </Link>
              </div>
            )
          })}
        </div>

        {showFaq && (
          <div className="mt-16">
            <h3 className="text-center font-display text-2xl font-bold tracking-display">
              {t('pricing.faq.title')}
            </h3>
            <dl className="mx-auto mt-8 grid max-w-[880px] gap-6 sm:grid-cols-2">
              {FAQS.map((faq) => (
                <div key={faq}>
                  <dt className="font-semibold text-text-primary">
                    {t(`pricing.faq.${faq}.question`)}
                  </dt>
                  <dd className="mt-1.5 text-[15px] leading-relaxed text-text-secondary">
                    {t(`pricing.faq.${faq}.answer`)}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        )}
      </div>
    </section>
  )
}

export default Pricing
